/*
 * Framing maths for viewports that are not the shape the design was drawn at.
 *
 * Kept pure and out of the components so `test.js` can hold the numbers to
 * account without a browser, which matters here more than usual: the whole point
 * of these is that they behave sensibly at aspect ratios nobody looks at while
 * developing, and an assertion is a cheaper way to check a 320x568 phone than
 * resizing a window and squinting at it.
 */

const DEG = Math.PI / 180;

/*
 * The horizontal field a pinhole camera needs in order to show exactly
 * `verticalFovDeg` of sky on a viewport of this shape.
 *
 * `makeCamera` takes a horizontal field and derives its focal length from the
 * width, which is the right way round for a screen that is wider than it is
 * tall: fix the horizontal field and the vertical one follows, smaller. Turn the
 * screen on its side and that same rule runs the other way — a fixed 105°
 * horizontal field on a 390x844 phone works out at 141° vertically, and since
 * the backdrop's camera sits 38° above the horizon, the bottom third of the
 * frame ends up pointed at the ground. That is exactly what the landing page was
 * doing: the stars stopped two thirds of the way down because there are no stars
 * below the horizon, and the "background doesn't cover the screen" bug was the
 * sky being drawn correctly and framed wrongly.
 *
 * So the vertical field is the thing held fixed and the horizontal one is
 * derived. On a 16:10 desktop window this changes nothing worth measuring — the
 * old fixed 105° horizontal came out at 80.1° vertical, so asking for 80° back
 * gives a focal length within 0.2% of what it already was — while on a phone it
 * keeps the frame pointed where the stars are.
 */
export function horizontalFovDeg(width, height, verticalFovDeg) {
  if (!(width > 0) || !(height > 0)) return verticalFovDeg;
  const halfV = Math.tan((verticalFovDeg / 2) * DEG);
  return (Math.atan((width / height) * halfV) / DEG) * 2;
}

/*
 * When the guess panel stops being a window and becomes a docked sheet.
 *
 * Two conditions, not one. The width test is the obvious phone case: a 460px
 * floating panel does not fit on a 390px screen, and shrinking it to fit leaves
 * a map too small to drop a pin on. The height test is the one that is easy to
 * miss and worse when it bites — a phone held sideways is 390px tall, the panel
 * is taller than that, and the stage clips its overflow, so the *submit button*
 * is simply not on the screen and the round cannot be finished at all.
 *
 * Exported as a string so both the stylesheet's breakpoints and the component's
 * `matchMedia` read from one place, and so a test can prove they agree.
 */
export const COMPACT_PANEL_QUERY = '(max-width: 700px), (max-height: 520px)';

/*
 * Where the docked sheet stands along the right-hand edge instead of lying
 * along the bottom.
 *
 * Height is what decides it: a bottom sheet takes about three quarters of the
 * stage, which on a 360-point-tall screen leaves a strip of sky too thin to
 * hold the compass, let alone read. Standing it up costs width instead, and
 * there is width to spare on any screen that is this much wider than it is tall.
 *
 * The width floor is the exception — below it there is not enough to divide, and
 * a 46% column would be too narrow to drop a pin on. Those sizes keep the bottom
 * sheet and simply are cramped, which is honest: it is a 550-point browser
 * window, not a phone anyone owns.
 */
export const DOCK_SIDEWAYS_QUERY = '(min-width: 560px) and (max-height: 520px)';
